import { useRef, useState } from "react";
import { API } from "@/lib/api";

// Records mic audio in ~25s segments (under Sarvam's 30s cap), transcribes each
// segment via the backend, and stitches the transcripts. Handles long answers.
const SEGMENT_MS = 25000;

export function useVoiceRecorder() {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const mrRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);     // chunks of the current segment
  const segmentsRef = useRef([]);   // completed segment blobs
  const timerRef = useRef(null);
  const stoppingRef = useRef(false);
  const resolveRef = useRef(null);
  const mimeRef = useRef("audio/webm");

  const pickMimeType = () => {
    const types = ["audio/webm", "audio/mp4", "audio/ogg"];
    if (typeof MediaRecorder === "undefined") return "";
    for (const t of types) {
      if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) return t;
    }
    return "";
  };

  const finalizeSegment = () => {
    const blob = new Blob(chunksRef.current, { type: mimeRef.current });
    chunksRef.current = [];
    if (blob.size > 0) segmentsRef.current.push(blob);
  };

  const uploadAll = async () => {
    const segs = segmentsRef.current;
    if (!segs.length) {
      setState("idle");
      resolveRef.current && resolveRef.current({ ok: false });
      return;
    }
    setState("transcribing");
    const ext = mimeRef.current.includes("mp4") ? "mp4" : mimeRef.current.includes("ogg") ? "ogg" : "webm";
    const texts = [];
    let lang = "";
    for (const blob of segs) {
      try {
        const form = new FormData();
        form.append("audio", blob, `voice.${ext}`);
        const res = await fetch(`${API}/voice/transcribe`, { method: "POST", body: form });
        if (!res.ok) continue;
        const data = await res.json();
        if (data.text) texts.push(data.text);
        if (!lang && data.detected_language) lang = data.detected_language;
      } catch (e) {
        // skip failed segment, keep the rest
      }
    }
    setState("idle");
    const text = texts.join(" ").trim();
    resolveRef.current && resolveRef.current(
      text ? { ok: true, text, detected_language: lang } : { ok: false }
    );
  };

  const handleSegmentStop = () => {
    finalizeSegment();
    if (!stoppingRef.current) {
      // rolling segment: restart recorder for the next chunk
      try {
        mrRef.current && mrRef.current.start();
      } catch (e) {
        /* ignore */
      }
      return;
    }
    // user stopped: clean up mic + transcribe everything
    if (streamRef.current) streamRef.current.getTracks().forEach((tr) => tr.stop());
    uploadAll();
  };

  const start = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("unsupported");
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    const mimeType = pickMimeType();
    const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    mimeRef.current = mr.mimeType || mimeType || "audio/webm";
    chunksRef.current = [];
    segmentsRef.current = [];
    stoppingRef.current = false;
    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    mr.onstop = handleSegmentStop;
    mrRef.current = mr;
    mr.start();
    setState("recording");
    // roll to a new segment every SEGMENT_MS so no single clip exceeds the STT cap
    timerRef.current = setInterval(() => {
      if (mrRef.current && mrRef.current.state === "recording") mrRef.current.stop();
    }, SEGMENT_MS);
  };

  // Returns a promise resolving with {ok, text, detected_language}
  const stop = () =>
    new Promise((resolve) => {
      resolveRef.current = resolve;
      stoppingRef.current = true;
      clearInterval(timerRef.current);
      const mr = mrRef.current;
      if (mr && mr.state !== "inactive") mr.stop();
      else uploadAll();
    });

  return { state, start, stop };
}
